import axios from 'axios';
import { globalYoutubeCache } from './globalYoutubeCache';

const API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;
const BASE_URL = 'https://www.googleapis.com/youtube/v3';

// 創建自定義的 axios 實例
const youtubeAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// 添加請求攔截器
youtubeAxios.interceptors.request.use((config: any) => {
  // 添加 API 金鑰到所有請求
  config.params = {
    ...config.params,
    key: API_KEY
  };
  return config;
});

// 添加響應攔截器
youtubeAxios.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    console.error('YouTube API 錯誤:', error.response?.data || error.message);
    throw error;
  }
);

interface YouTubeApiResponse {
  items: Array<{
    snippet: {
      title: string;
      description: string;
      publishedAt: string;
      thumbnails: {
        default: { url: string; width: number; height: number };
        medium: { url: string; width: number; height: number };
        high: { url: string; width: number; height: number };
        standard?: { url: string; width: number; height: number };
        maxres?: { url: string; width: number; height: number };
      };
      channelTitle: string;
    };
    contentDetails: {
      duration: string;
    };
    statistics: {
      viewCount: string;
    };
  }>;
}

export interface VideoDetails {
  title: string;
  description: string;
  publishedAt: string;
  thumbnails: {
    default: { url: string; width: number; height: number };
    medium: { url: string; width: number; height: number };
    high: { url: string; width: number; height: number };
    standard?: { url: string; width: number; height: number };
    maxres?: { url: string; width: number; height: number };
  };
  channelTitle: string;
  duration: string;
  viewCount: string;
  tags?: string[]; // 新增 tags 欄位
}

export interface CaptionInfo {
  id: string;
  language: string;
  trackKind: string;
  isAutoSynced: boolean;
  isDefault: boolean;
  isCC: boolean;
  name: string;
}

export interface CaptionsResponse {
  videoId: string;
  captions: CaptionInfo[];
  totalCount: number;
}

// 解碼文字函數
const decodeText = (text: string): string => {
  if (!text) return '';
  try {
    // 處理可能的 HTML 實體和編碼問題
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec, 10)));
  } catch (error) {
    console.error('解碼文字時發生錯誤:', error);
    return text;
  }
};

export const youtubeApi = {
  // 從 URL 中提取視頻 ID（支援 YouTube Shorts 和 Live）
  extractVideoId: (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,  // 🔧 新增：支援直播格式
      /youtu\.be\/([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    
    return null;
  },

  // 獲取視頻詳細信息（使用全局快取）
  getVideoDetails: async (videoId: string): Promise<VideoDetails> => {
    if (videoId.startsWith('blob:')) {
      throw new Error('不支援 blob: 影片來源，請確認傳入的是 YouTube 影片網址或 ID');
    }
    
    // 如果傳入的是完整 URL，先提取 YouTube 影片 ID
    let cleanVideoId = videoId;
    if (videoId.includes('http') || videoId.includes('drive.google.com') || videoId.includes('blob:')) {
      const extractedId = youtubeApi.extractVideoId(videoId);
      if (!extractedId) {
        // 如果不是 YouTube URL，直接使用原始 ID
        console.log(`[YouTube API] 無法從 URL 中提取 YouTube 影片 ID，使用原始 ID: ${videoId}`);
        cleanVideoId = videoId;
      } else {
        cleanVideoId = extractedId;
      }
    }

    // 🎯 使用全局快取，確保每支影片每天只請求一次
    return await globalYoutubeCache.getVideoInfo(cleanVideoId, async () => {
      console.log(`🔄 調用 YouTube API: ${cleanVideoId}`);
      
      try {
        // 直接使用後端 API
        const response = await axios.get<VideoDetails>(`/api/youtube-title/${cleanVideoId}`, {
          params: {
            fields: 'snippet,contentDetails,statistics'
          },
          headers: {
            'Accept': 'application/json'
          }
        });

        const video = response.data;
        if (!video) {
          throw new Error('視頻不存在或無法訪問');
        }

        // 確保所有文字都正確解碼
        const processedVideo = {
          ...video,
          title: decodeText(video.title || ''),
          description: decodeText(video.description || ''),
          channelTitle: decodeText(video.channelTitle || ''),
          duration: video.duration || '0:00',
          viewCount: video.viewCount || '0',
          tags: video.tags || [] // 確保 tags 欄位存在
        };

        console.log(`✅ YouTube API 請求成功: ${cleanVideoId} - ${processedVideo.title}`);
        return processedVideo;
      } catch (error) {
        console.error('YouTube API 請求失敗:', error, (error as any)?.response);
        throw error;
      }
    });
  },

  // 獲取視頻的所有可用縮圖
  getThumbnails: async (videoId: string): Promise<string[]> => {
    try {
      const details = await youtubeApi.getVideoDetails(videoId);
      return Object.values(details.thumbnails)
        .map(thumbnail => thumbnail.url)
        .filter((url): url is string => !!url);
    } catch (error) {
      console.error('獲取視頻縮圖失敗:', error);
      throw error;
    }
  },

  // 測試 API 是否正常工作
  testApiConnection: async (): Promise<boolean> => {
    try {
      const testVideoId = 'dQw4w9WgXcQ';
      await youtubeApi.getVideoDetails(testVideoId);
      return true;
    } catch (error) {
      console.error('API 連接測試失敗:', error);
      return false;
    }
  },

  // 獲取影片的字幕列表
  getCaptions: async (videoId: string): Promise<CaptionsResponse> => {
    try {
      // 如果傳入的是完整 URL，先提取 YouTube 影片 ID
      let cleanVideoId = videoId;
      if (videoId.includes('http') || videoId.includes('drive.google.com') || videoId.includes('blob:')) {
        const extractedId = youtubeApi.extractVideoId(videoId);
        if (!extractedId) {
          // 如果不是 YouTube URL，直接使用原始 ID
          console.log(`[YouTube Captions API] 無法從 URL 中提取 YouTube 影片 ID，使用原始 ID: ${videoId}`);
          cleanVideoId = videoId;
        } else {
          cleanVideoId = extractedId;
        }
      }
      

      
      const response = await axios.get<CaptionsResponse>(`/api/youtube-captions/${cleanVideoId}`, {
        headers: {
          'Accept': 'application/json'
        }
      });



      return response.data;
    } catch (error) {
      console.error('YouTube Captions API 請求失敗:', error, (error as any)?.response);
      throw error;
    }
  },

  // 檢查影片是否有字幕
  hasCaptions: async (videoId: string): Promise<boolean> => {
    try {
      const captions = await youtubeApi.getCaptions(videoId);
      return captions.totalCount > 0;
    } catch (error) {
      console.error('檢查字幕失敗:', error);
      return false;
    }
  },

  // 獲取最佳字幕（優先中文，其次英文）
  getBestCaption: async (videoId: string): Promise<CaptionInfo | null> => {
    try {
      const captions = await youtubeApi.getCaptions(videoId);
      if (captions.captions.length === 0) {
        return null;
      }
      
      // 返回第一個（已經按優先順序排序）
      return captions.captions[0];
    } catch (error) {
      console.error('獲取最佳字幕失敗:', error);
      return null;
    }
  },

  // 獲取特定語言的字幕
  getCaptionByLanguage: async (videoId: string, language: string): Promise<CaptionInfo | null> => {
    try {
      const captions = await youtubeApi.getCaptions(videoId);
      const targetCaption = captions.captions.find(caption => 
        caption.language === language || 
        caption.language.startsWith(language)
      );
      
      return targetCaption || null;
    } catch (error) {
      console.error('獲取特定語言字幕失敗:', error);
      return null;
    }
  }
};